import React, { useCallback, useEffect, useMemo } from "react";

import { useParams } from "react-router-dom";

import AccountPlansTab from "./AccountPlansTab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import {
  ActiveCustomerWithSponsorsItem,
  fetchAccountsWithSponsors,
} from "../../Store/Reducers/OpsSlice";

/**
 * The list rows need only an account's id, name, industry, revenue and renewal;
 * the portfolio-only fields stay empty (spec NAV-08).
 */
export const toPlanRow = (
  item: ActiveCustomerWithSponsorsItem
): PortfolioAccountRow => ({
  customer_id: item.customer_id,
  customer_name: item.name,
  industry: item.industry,
  tier: item.tiers || null,
  role_type: "",
  region: item.region,
  location: item.location,
  al_level: null,
  al_label: null,
  al_published_days: null,
  rl_aggregate: null,
  rl_band: null,
  my_rl: null,
  team_rl: null,
  mapped_pairs: 0,
  assessed_pairs: 0,
  pending_pairs: 0,
  total_possible_pairs: 0,
  revenue: item.revenue ?? null,
  renewal_date: item.renewal,
  renewal_days: null,
  account_health: null,
  cluster: null,
  business_health: null,
  business_health_label: null,
  needs_attention: false,
  attention_reasons: [],
  primary_sponsors: item.primary_sponsors,
  primary_sponsors_id: item.primary_sponsors_id,
  secondary_sponsors: item.secondary_sponsors,
  secondary_sponsors_id: item.secondary_sponsors_id,
  self_assessment_status: null,
  last_assessment: null,
});

/**
 * Exec Ops → Account Plans: the organization admin's way in (spec NAV-08). Admins
 * have no My Accounts, so the Plans list runs over every account in the
 * organization; Activity is the same org-wide ledger the team sees.
 */
const OpsAccountPlansTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { data, loading, error } = useAppSelector(state => state.ops.accounts);

  useEffect(() => {
    void dispatch(fetchAccountsWithSponsors({}));
  }, [dispatch]);

  const accounts = useMemo(
    () => (data?.items || []).filter(a => a.is_active !== false).map(toPlanRow),
    [data]
  );
  const withOrg = useCallback(
    (path: string) => {
      const normalized = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalized}` : normalized;
    },
    [orgSlug]
  );

  return (
    <AccountPlansTab
      accounts={accounts}
      withOrg={withOrg}
      loading={loading || (!data && !error)}
      orgWide
    />
  );
};

export default OpsAccountPlansTab;
